import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError, apiMfaSendCode, isMfaChallenge, type MfaChallenge } from '../lib/api';
import { Alert, Button, Field, PasswordInput, TextInput } from '../components/ui';
import { homeFor, OWNER_ROOT, PORTAL_ROOT, PROVIDER_ROOT, usePortal } from '../lib/portal';
import '../styles/auth.css';

import { EnvBadge } from '../components/EnvBadge';
/**
 * One door, and the role decides which room.
 *
 * There are three places to land — the landlord app, the Control Center, the
 * Tenant Portal — and a separate branded sign-in page for each was the
 * alternative. This is the safer half of that choice: one auth surface is one
 * thing to keep correct, and a tenant who bookmarks the landlord URL, or
 * follows a link a landlord sent them, still ends up where they belong instead
 * of at a form that refuses them for reasons it cannot explain.
 *
 * The redirect a guard asked for still wins when it points somewhere this
 * person can actually go. A tenant sent here from /portal/invoices returns to
 * /portal/invoices; a tenant sent here from /properties does not get handed
 * back a landlord page, because it was never theirs to see.
 */
export function Login() {
  const { siteHome } = usePortal();
  const { login, completeMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: string; reason?: string } | null;
  const from = state?.from ?? '/';

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  // Set when the password was right but a second factor is due. While it is
  // set the form shows the six-digit step instead; nothing is signed in yet.
  const [challenge, setChallenge] = useState<string | null>(null);
  // Which ways through the second step this person has, from the same answer.
  const [factors, setFactors] = useState<MfaChallenge['factors']>(undefined);
  // Set once a text or email has gone out, so the screen can say where.
  const [sentTo, setSentTo] = useState<{ way: 'sms' | 'email'; to: string } | null>(null);
  const [sending, setSending] = useState<'sms' | 'email' | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Read once: a page refresh on /login must not keep re-showing this after
  // the moment it describes has passed, and history.state survives a refresh.
  const [idleNotice] = useState(() => state?.reason === 'idle');
  const [busy, setBusy] = useState(false);

  const sendCode = async (way: 'sms' | 'email') => {
    if (!challenge) return;
    setError(null); setSending(way);
    try {
      const r = await apiMfaSendCode(way, challenge);
      setSentTo({ way, to: r.to });
      setCode('');
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401
        ? 'That took too long. Sign in again.'
        : err instanceof ApiError ? err.message : 'Could not send the code. Please try again.');
    } finally {
      setSending(null);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const session = challenge
        ? await completeMfa(challenge, code.trim())
        : await login(identifier.trim(), password);
      if (isMfaChallenge(session)) {
        // Password accepted; now the code. The challenge token lives only in
        // component state and dies with the page.
        setChallenge(session.mfaToken);
        setFactors(session.factors);
        setSentTo(null);
        setBusy(false);
        return;
      }
      const home = homeFor(session.roles);
      /*
       * The intended destination, but only if it belongs to the portal this
       * person signs in to. Otherwise their own front door.
       *
       * Phrased against whichever portal they belong to, not against the tenant
       * one by name. Written as `home !== PORTAL_ROOT || ...` this was true for
       * every owner, so an owner who was bounced here from a landlord URL got
       * sent straight back to it after signing in — to a page their account
       * cannot load.
       */
      const ownPortal = home === PORTAL_ROOT || home === OWNER_ROOT || home === PROVIDER_ROOT;
      const wanted = from !== '/' && (!ownPortal || from.startsWith(home));
      navigate(wanted ? from : home, { replace: true });
    } catch (err) {
      // One message that never reveals whether the identifier exists. The
      // server enforces this too (enumeration resistance); the UI matches it.
      if (err instanceof ApiError && err.status === 429) {
        setError(err.message); // lockout message carries the wait time
      } else if (err instanceof ApiError && err.code === 'network') {
        setError('Signing in needs a connection. Check your network and try again.');
      } else if (challenge && err instanceof ApiError && err.status === 401 && err.code !== 'invalid_mfa_code') {
        // The five-minute challenge expired: back to the password.
        setChallenge(null); setCode('');
        setError('That took too long. Sign in again.');
      } else if (challenge) {
        setError(sentTo ? 'That code is not valid. Check the latest message and try again.'
          : 'That code is not valid. Check your authenticator app and try again.');
      } else {
        setError('That email or phone and password do not match. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-brand-row">
        <Link to={siteHome} className="auth-brand">NyumbaSuite</Link>
        <EnvBadge />
      </div>
      <form className="auth-card login" onSubmit={submit} noValidate>
        <h1 className="auth-title">Sign in</h1>
        <p className="auth-lede">Landlords, agents and tenants — one sign-in.</p>

        {error && <div style={{ marginBottom: 16 }}><Alert tone="danger">{error}</Alert></div>}
        {!error && idleNotice && (
          <div style={{ marginBottom: 16 }}>
            <Alert tone="info">You were signed out after 30 minutes of inactivity. Sign in again to continue.</Alert>
          </div>
        )}

        {challenge ? (
          <>
            <Field label={sentTo || (factors && !factors.authenticator) ? 'Sign-in code' : 'Authenticator code'} htmlFor="code"
              hint={sentTo
                ? `We sent a code ${sentTo.way === 'sms' ? 'by text message to' : 'by email to'} ${sentTo.to}. It works once and lasts ten minutes.`
                : factors && !factors.authenticator
                  ? 'Ask for a code below, then type the six digits we send you.'
                  : 'The six digits from your authenticator app. Codes change every 30 seconds.'}>
              <TextInput id="code" name="code" value={code} autoComplete="one-time-code"
                inputMode="numeric" pattern="[0-9]*" maxLength={6} autoFocus
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required />
            </Field>
            <Button type="submit" block loading={busy} disabled={code.length !== 6}>Verify</Button>
            {factors?.sms && (
              <Button type="button" variant="secondary" block loading={sending === 'sms'}
                onClick={() => sendCode('sms')} style={{ marginTop: 8 }}>
                {sentTo?.way === 'sms' ? 'Send another text' : 'Text me a code'}
              </Button>
            )}
            {factors?.email && (
              <Button type="button" variant="ghost" block loading={sending === 'email'}
                onClick={() => sendCode('email')} style={{ marginTop: 8 }}>
                {sentTo?.way === 'email' ? 'Send another code' : 'Email me a code'}
              </Button>
            )}
            <div className="auth-foot">
              <button type="button" className="crumb"
                onClick={() => { setChallenge(null); setFactors(undefined); setSentTo(null); setCode(''); setError(null); }}>
                ← Back to sign in
              </button>
            </div>
          </>
        ) : (
          <>
            <Field label="Email or phone" htmlFor="identifier">
              <TextInput id="identifier" name="identifier" value={identifier} autoComplete="username"
                inputMode="email" autoFocus onChange={(e) => setIdentifier(e.target.value)} required />
            </Field>

            <Field label="Password" htmlFor="password">
              <PasswordInput id="password" name="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required />
            </Field>

            <label className="check" style={{ marginBottom: 20 }}>
              <input type="checkbox" name="remember" /> Remember this device
            </label>

            <Button type="submit" block loading={busy}>Sign in</Button>
          </>
        )}

        <div className="auth-foot">
          <Link to="/forgot-password">Forgot password?</Link>
          <span> · </span>
          <Link to="/register">Create an account</Link>
        </div>

        {/* Said here because this is where a tenant looks for it. "Create an
            account" above makes a LANDLORD account, and a tenant who used it
            would end up owning an empty portfolio instead of reaching their
            own home — a dead end that looks like success. */}
        <p className="auth-note">
          Renting a home? Your landlord sends you an invitation — there is no
          tenant sign-up.
        </p>
      </form>
      <Link className="auth-link" to={siteHome}>← Back to homepage</Link>
    </div>
  );
}
